/**
 * Installing the app (PWA). The browser announces that it can install with one
 * `beforeinstallprompt` event, early and only once per page load – so it is caught here, at
 * start, and kept for whoever offers the installation later (the hint on the dashboard, the card
 * in the profile). Safari on iPhone and iPad never sends it: there the user adds the app through
 * the share menu, and all we can do is say how.
 */

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallMode =
  /** Runs as an installed app already. */
  | 'installed'
  /** The browser offers the installation: one button. */
  | 'prompt'
  /** iPhone / iPad: through the share menu, by hand. */
  | 'ios'
  /** This browser cannot install (or has not offered it yet). */
  | 'unavailable';

export interface InstallState {
  mode: InstallMode;
  /** The hint was closed; the profile still offers the installation. */
  dismissed: boolean;
}

const DISMISSED_KEY = 'rm.installDismissed';

let deferred: BeforeInstallPromptEvent | null = null;
let installedNow = false;
let started = false;
const listeners = new Set<() => void>();
let snapshot: InstallState = { mode: 'unavailable', dismissed: false };

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED_KEY) === '1';
  } catch {
    return false;
  }
}

export function isStandalone(): boolean {
  if (typeof window === 'undefined') return false;
  const iosStandalone = (navigator as Navigator & { standalone?: boolean }).standalone === true;
  return (
    iosStandalone ||
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(display-mode: standalone)').matches)
  );
}

/** iPhone, iPod and iPad – the latter calls itself a Mac but has a touch screen. */
export function isIos(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iphone|ipad|ipod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
  );
}

function compute(): InstallState {
  const mode: InstallMode =
    installedNow || isStandalone()
      ? 'installed'
      : deferred
        ? 'prompt'
        : isIos()
          ? 'ios'
          : 'unavailable';
  return { mode, dismissed: readDismissed() };
}

function publish(): void {
  const next = compute();
  if (next.mode === snapshot.mode && next.dismissed === snapshot.dismissed) return;
  snapshot = next;
  listeners.forEach((listener) => listener());
}

/** Called once when the app starts – before React, so the one event is never missed. */
export function startInstallCapture(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  window.addEventListener('beforeinstallprompt', (event) => {
    // Keep the browser's own mini-infobar away; the app offers the installation itself.
    event.preventDefault();
    deferred = event as BeforeInstallPromptEvent;
    publish();
  });
  window.addEventListener('appinstalled', () => {
    deferred = null;
    installedNow = true;
    publish();
  });
  snapshot = compute();
}

export function subscribeInstall(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function installSnapshot(): InstallState {
  return snapshot;
}

/**
 * Shows the browser's installation dialog. The event can be used once; after "no" the browser
 * decides when it offers the installation again (usually at the next visit).
 */
export async function promptInstall(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  const event = deferred;
  if (!event) return 'unavailable';
  deferred = null;
  await event.prompt();
  const { outcome } = await event.userChoice;
  if (outcome === 'accepted') installedNow = true;
  publish();
  return outcome;
}

/** The hint was closed. It stays away on this device; the profile keeps offering it. */
export function dismissInstallHint(): void {
  try {
    localStorage.setItem(DISMISSED_KEY, '1');
  } catch {
    // storage unavailable: the hint is gone for this page view only
  }
  publish();
}

/** For tests. */
export function resetInstallForTests(): void {
  deferred = null;
  installedNow = false;
  snapshot = compute();
  listeners.forEach((listener) => listener());
}
